/**
 * Unified selection mask helpers for Image Editor Tier 2 tools.
 * Selection = { width, height, mask: Uint8Array } where 1 = selected.
 */

/**
 * @typedef {{ width: number, height: number, mask: Uint8Array }} SelectionMask
 */

/**
 * @param {number} width
 * @param {number} height
 * @returns {SelectionMask}
 */
export function createEmptySelection(width, height) {
  const w = Math.max(1, Math.floor(width) || 1);
  const h = Math.max(1, Math.floor(height) || 1);
  return { width: w, height: h, mask: new Uint8Array(w * h) };
}

/**
 * Shallow structural clone (copies the mask bytes).
 * @param {SelectionMask|null|undefined} sel
 * @returns {SelectionMask|null}
 */
export function cloneSelectionMask(sel) {
  if (!sel?.mask) return null;
  return {
    width: sel.width,
    height: sel.height,
    mask: new Uint8Array(sel.mask),
  };
}

/**
 * Return a writable selection matching canvas size (clone existing or empty).
 * @param {SelectionMask|null|undefined} sel
 * @param {number} width
 * @param {number} height
 * @returns {SelectionMask}
 */
export function ensureSelectionSize(sel, width, height) {
  const w = Math.max(1, Math.floor(width) || 1);
  const h = Math.max(1, Math.floor(height) || 1);
  if (sel?.mask && sel.width === w && sel.height === h) {
    return cloneSelectionMask(sel);
  }
  return createEmptySelection(w, h);
}

/**
 * Hard circular brush dab into a selection mask (mutates in place).
 * Same diameter convention as Paint / retouch brushes.
 * @param {SelectionMask} sel
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize diameter in px
 * @param {boolean} [erase=false] subtract (0) instead of add (1)
 * @returns {boolean} true if any pixel changed
 */
export function paintSelectionBrushDab(sel, cx, cy, brushSize, erase = false) {
  if (!sel?.mask) return false;
  const { width, height, mask } = sel;
  const radius = Math.max(0.5, Number(brushSize) / 2);
  const rCeil = Math.ceil(radius);
  const r2 = radius * radius;
  const value = erase ? 0 : 1;
  const x0 = Math.max(0, Math.floor(cx - rCeil));
  const y0 = Math.max(0, Math.floor(cy - rCeil));
  const x1 = Math.min(width - 1, Math.ceil(cx + rCeil));
  const y1 = Math.min(height - 1, Math.ceil(cy + rCeil));
  let changed = false;
  for (let y = y0; y <= y1; y += 1) {
    const row = y * width;
    for (let x = x0; x <= x1; x += 1) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      if (dx * dx + dy * dy > r2) continue;
      const idx = row + x;
      if (mask[idx] === value) continue;
      mask[idx] = value;
      changed = true;
    }
  }
  return changed;
}

/**
 * @param {SelectionMask|null|undefined} sel
 * @returns {boolean}
 */
export function selectionHasPixels(sel) {
  if (!sel?.mask) return false;
  for (let i = 0; i < sel.mask.length; i += 1) {
    if (sel.mask[i]) return true;
  }
  return false;
}

/**
 * @param {SelectionMask|null|undefined} sel
 * @returns {number}
 */
export function countSelectionPixels(sel) {
  if (!sel?.mask) return 0;
  let n = 0;
  for (let i = 0; i < sel.mask.length; i += 1) {
    if (sel.mask[i]) n += 1;
  }
  return n;
}

/**
 * Paint selection fill + boundary into a 1:1 canvas context (CSS overlay only).
 * @param {CanvasRenderingContext2D} ctx
 * @param {SelectionMask} sel
 */
export function paintSelectionMaskOverlay(ctx, sel) {
  if (!ctx || !sel?.mask) return;
  const { width, height, mask } = sel;
  const img = ctx.createImageData(width, height);
  const d = img.data;
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      const i = row + x;
      if (!mask[i]) continue;
      const o = i * 4;
      const edge =
        x === 0 ||
        y === 0 ||
        x === width - 1 ||
        y === height - 1 ||
        !mask[i - 1] ||
        !mask[i + 1] ||
        !mask[i - width] ||
        !mask[i + width];
      if (edge) {
        d[o] = 255;
        d[o + 1] = 255;
        d[o + 2] = 255;
        d[o + 3] = 230;
      } else {
        d[o] = 0;
        d[o + 1] = 229;
        d[o + 2] = 255;
        d[o + 3] = 48;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Moore-neighborhood outer/hole contours for dashed SVG outlines.
 * Coordinates are pixel centers (compatible with canvas overlay scale).
 * @param {SelectionMask} sel
 * @returns {Array<Array<{x:number,y:number}>>}
 */
export function traceSelectionContours(sel) {
  if (!sel?.mask) return [];
  const { width: w, height: h, mask } = sel;
  const inside = (x, y) =>
    x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1;
  /** @type {Uint8Array} */
  const started = new Uint8Array(w * h);
  /** 0=E,1=SE,2=S,3=SW,4=W,5=NW,6=N,7=NE */
  const DX = [1, 1, 0, -1, -1, -1, 0, 1];
  const DY = [0, 1, 1, 1, 0, -1, -1, -1];
  /** @type {Array<Array<{x:number,y:number}>>} */
  const rings = [];

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (!inside(x, y) || inside(x - 1, y)) continue;
      const startKey = y * w + x;
      if (started[startKey]) continue;

      /** @type {Array<{x:number,y:number}>} */
      const ring = [];
      let cx = x;
      let cy = y;
      let dir = 4; // arrived facing west → next search from south-west
      const maxSteps = w * h * 2 + 8;
      let steps = 0;
      do {
        started[cy * w + cx] = 1;
        ring.push({ x: cx + 0.5, y: cy + 0.5 });
        let found = false;
        // Start search from dir+6 (turn left relative to arrival)
        for (let k = 0; k < 8; k += 1) {
          const nd = (dir + 6 + k) % 8;
          const nx = cx + DX[nd];
          const ny = cy + DY[nd];
          if (!inside(nx, ny)) continue;
          cx = nx;
          cy = ny;
          dir = nd;
          found = true;
          break;
        }
        if (!found) break;
        steps += 1;
      } while ((cx !== x || cy !== y) && steps < maxSteps);

      if (ring.length >= 3) {
        ring.push({ x: ring[0].x, y: ring[0].y });
        rings.push(ring);
      }
    }
  }
  return rings;
}

/**
 * @param {SelectionMask} sel
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null}
 */
export function selectionAABB(sel) {
  if (!sel?.mask) return null;
  const { width: w, height: h, mask } = sel;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (!mask[y * w + x]) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * @param {number} width
 * @param {number} height
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @returns {SelectionMask}
 */
export function rectSelectionMask(width, height, x0, y0, x1, y1) {
  const sel = createEmptySelection(width, height);
  const left = Math.max(0, Math.min(Math.round(x0), Math.round(x1)));
  const right = Math.min(width - 1, Math.max(Math.round(x0), Math.round(x1)));
  const top = Math.max(0, Math.min(Math.round(y0), Math.round(y1)));
  const bottom = Math.min(height - 1, Math.max(Math.round(y0), Math.round(y1)));
  for (let y = top; y <= bottom; y += 1) {
    const row = y * width;
    for (let x = left; x <= right; x += 1) {
      sel.mask[row + x] = 1;
    }
  }
  return sel;
}

/**
 * @param {number} width
 * @param {number} height
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @returns {SelectionMask}
 */
export function ellipseSelectionMask(width, height, x0, y0, x1, y1) {
  const sel = createEmptySelection(width, height);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const rx = Math.abs(x1 - x0) / 2;
  const ry = Math.abs(y1 - y0) / 2;
  if (rx < 0.5 || ry < 0.5) return sel;
  const left = Math.max(0, Math.floor(cx - rx));
  const right = Math.min(width - 1, Math.ceil(cx + rx));
  const top = Math.max(0, Math.floor(cy - ry));
  const bottom = Math.min(height - 1, Math.ceil(cy + ry));
  const rx2 = rx * rx;
  const ry2 = ry * ry;
  for (let y = top; y <= bottom; y += 1) {
    const dy = y + 0.5 - cy;
    const row = y * width;
    for (let x = left; x <= right; x += 1) {
      const dx = x + 0.5 - cx;
      if (dx * dx / rx2 + dy * dy / ry2 <= 1) {
        sel.mask[row + x] = 1;
      }
    }
  }
  return sel;
}

/**
 * Even-odd / winding fill for freehand lasso polygon.
 * @param {number} width
 * @param {number} height
 * @param {Array<{x:number,y:number}>} points
 * @returns {SelectionMask}
 */
export function lassoSelectionMask(width, height, points) {
  const sel = createEmptySelection(width, height);
  if (!Array.isArray(points) || points.length < 3) return sel;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.max(0, Math.floor(Math.min(...xs)));
  const maxX = Math.min(width - 1, Math.ceil(Math.max(...xs)));
  const minY = Math.max(0, Math.floor(Math.min(...ys)));
  const maxY = Math.min(height - 1, Math.ceil(Math.max(...ys)));
  const n = points.length;
  for (let y = minY; y <= maxY; y += 1) {
    const row = y * width;
    for (let x = minX; x <= maxX; x += 1) {
      // Ray cast
      let inside = false;
      for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
        const xi = points[i].x;
        const yi = points[i].y;
        const xj = points[j].x;
        const yj = points[j].y;
        const intersect =
          (yi > y) !== (yj > y) &&
          x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0000001) + xi;
        if (intersect) inside = !inside;
      }
      if (inside) sel.mask[row + x] = 1;
    }
  }
  return sel;
}

/**
 * Sobel gradient magnitude on luminance (for magnetic lasso v1).
 * @param {ImageData} imageData
 * @returns {Float32Array} length width*height
 */
export function sobelMagnitude(imageData) {
  const w = imageData.width;
  const h = imageData.height;
  const data = imageData.data;
  const luma = new Float32Array(w * h);
  for (let i = 0, p = 0; i < luma.length; i += 1, p += 4) {
    luma[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  }
  const out = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      const gx =
        -luma[i - w - 1] +
        luma[i - w + 1] -
        2 * luma[i - 1] +
        2 * luma[i + 1] -
        luma[i + w - 1] +
        luma[i + w + 1];
      const gy =
        -luma[i - w - 1] -
        2 * luma[i - w] -
        luma[i - w + 1] +
        luma[i + w - 1] +
        2 * luma[i + w] +
        luma[i + w + 1];
      out[i] = Math.hypot(gx, gy);
    }
  }
  return out;
}

/**
 * Magnetic lasso v1: snap (cx,cy) to strongest local edge within radius.
 * Trade-off vs Photoshop livewire: local Sobel max search only — no global
 * Dijkstra path along the whole contour.
 * @param {Float32Array} magnitude from sobelMagnitude
 * @param {number} width
 * @param {number} height
 * @param {number} cx
 * @param {number} cy
 * @param {number} [radius=10]
 * @returns {{x:number,y:number}}
 */
export function snapPointToEdge(magnitude, width, height, cx, cy, radius = 10) {
  const r = Math.max(1, Math.round(Number(radius) || 10));
  const x0 = Math.max(0, Math.floor(cx - r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const x1 = Math.min(width - 1, Math.ceil(cx + r));
  const y1 = Math.min(height - 1, Math.ceil(cy + r));
  let bestScore = -1;
  let bestX = Math.max(0, Math.min(width - 1, Math.round(cx)));
  let bestY = Math.max(0, Math.min(height - 1, Math.round(cy)));
  const r2 = r * r;
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > r2) continue;
      const mag = magnitude[y * width + x] || 0;
      // Prefer strong edges; small distance penalty keeps path smoother.
      const score = mag - Math.sqrt(dx * dx + dy * dy) * 0.75;
      if (score > bestScore) {
        bestScore = score;
        bestX = x;
        bestY = y;
      }
    }
  }
  return { x: bestX, y: bestY };
}

/**
 * Contiguous color flood into a selection mask (magic wand).
 * @param {ImageData} imageData
 * @param {number} startX
 * @param {number} startY
 * @param {number} tolerance
 * @param {(a:{r:number,g:number,b:number}, b:{r:number,g:number,b:number}) => number} colorDistance
 * @returns {SelectionMask}
 */
export function magicWandSelection(
  imageData,
  startX,
  startY,
  tolerance,
  colorDistance,
) {
  const w = imageData.width;
  const h = imageData.height;
  const sel = createEmptySelection(w, h);
  const sx = Math.max(0, Math.min(w - 1, Math.round(startX)));
  const sy = Math.max(0, Math.min(h - 1, Math.round(startY)));
  const data = imageData.data;
  const si = (sy * w + sx) * 4;
  const target = { r: data[si], g: data[si + 1], b: data[si + 2] };
  const tol = Math.max(0, Number(tolerance) || 0);
  const visited = new Uint8Array(w * h);
  /** @type {number[]} */
  const stack = [sy * w + sx];
  visited[sy * w + sx] = 1;

  while (stack.length) {
    const idx = stack.pop();
    const x = idx % w;
    const y = (idx - x) / w;
    const pi = idx * 4;
    const px = { r: data[pi], g: data[pi + 1], b: data[pi + 2] };
    if (colorDistance(px, target) > tol) continue;
    sel.mask[idx] = 1;
    const neighbors = [
      idx - 1,
      idx + 1,
      idx - w,
      idx + w,
    ];
    for (let n = 0; n < neighbors.length; n += 1) {
      const ni = neighbors[n];
      if (ni < 0 || ni >= w * h || visited[ni]) continue;
      const nx = ni % w;
      const ny = (ni - nx) / w;
      // Prevent wrap on horizontal
      if (Math.abs(nx - x) + Math.abs(ny - y) !== 1) continue;
      visited[ni] = 1;
      stack.push(ni);
    }
  }
  return sel;
}

/**
 * Flood-fill paint onto ImageData in place (paint bucket).
 * @param {ImageData} imageData
 * @param {number} startX
 * @param {number} startY
 * @param {{r:number,g:number,b:number,a?:number}} fillColor
 * @param {number} tolerance
 * @param {(a:{r:number,g:number,b:number}, b:{r:number,g:number,b:number}) => number} colorDistance
 * @returns {boolean} true if any pixel changed
 */
export function floodFillImageData(
  imageData,
  startX,
  startY,
  fillColor,
  tolerance,
  colorDistance,
) {
  const w = imageData.width;
  const h = imageData.height;
  const sx = Math.max(0, Math.min(w - 1, Math.round(startX)));
  const sy = Math.max(0, Math.min(h - 1, Math.round(startY)));
  const data = imageData.data;
  const si = (sy * w + sx) * 4;
  const target = {
    r: data[si],
    g: data[si + 1],
    b: data[si + 2],
    a: data[si + 3],
  };
  const fr = fillColor.r;
  const fg = fillColor.g;
  const fb = fillColor.b;
  const fa = fillColor.a == null ? 255 : fillColor.a;
  if (
    target.r === fr &&
    target.g === fg &&
    target.b === fb &&
    target.a === fa
  ) {
    return false;
  }
  const tol = Math.max(0, Number(tolerance) || 0);
  const visited = new Uint8Array(w * h);
  /** @type {number[]} */
  const stack = [sy * w + sx];
  visited[sy * w + sx] = 1;
  let changed = false;

  while (stack.length) {
    const idx = stack.pop();
    const x = idx % w;
    const y = (idx - x) / w;
    const pi = idx * 4;
    const px = { r: data[pi], g: data[pi + 1], b: data[pi + 2] };
    // Also require similar alpha so transparent vs opaque don't merge oddly
    if (Math.abs(data[pi + 3] - target.a) > tol) continue;
    if (colorDistance(px, target) > tol) continue;
    data[pi] = fr;
    data[pi + 1] = fg;
    data[pi + 2] = fb;
    data[pi + 3] = fa;
    changed = true;
    const neighbors = [idx - 1, idx + 1, idx - w, idx + w];
    for (let n = 0; n < neighbors.length; n += 1) {
      const ni = neighbors[n];
      if (ni < 0 || ni >= w * h || visited[ni]) continue;
      const nx = ni % w;
      const ny = (ni - nx) / w;
      if (Math.abs(nx - x) + Math.abs(ny - y) !== 1) continue;
      visited[ni] = 1;
      stack.push(ni);
    }
  }
  return changed;
}

/**
 * Extract a tight ImageData crop of selected pixels (unselected → alpha 0).
 * @param {ImageData} source
 * @param {SelectionMask} sel
 * @returns {{ imageData: ImageData, offsetX: number, offsetY: number }|null}
 */
export function extractSelectionImageData(source, sel) {
  const box = selectionAABB(sel);
  if (!box || !source) return null;
  const w = box.maxX - box.minX + 1;
  const h = box.maxY - box.minY + 1;
  const out = new ImageData(w, h);
  const sw = source.width;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const sx = box.minX + x;
      const sy = box.minY + y;
      const mi = sy * sel.width + sx;
      if (!sel.mask[mi]) continue;
      const si = (sy * sw + sx) * 4;
      const di = (y * w + x) * 4;
      out.data[di] = source.data[si];
      out.data[di + 1] = source.data[si + 1];
      out.data[di + 2] = source.data[si + 2];
      out.data[di + 3] = source.data[si + 3];
    }
  }
  return { imageData: out, offsetX: box.minX, offsetY: box.minY };
}

/**
 * Clear (alpha 0) selected pixels on a clone of source.
 * @param {ImageData} source
 * @param {SelectionMask} sel
 * @returns {ImageData}
 */
export function clearSelectionPixels(source, sel) {
  const out = new ImageData(
    new Uint8ClampedArray(source.data),
    source.width,
    source.height,
  );
  const w = sel.width;
  for (let i = 0; i < sel.mask.length; i += 1) {
    if (!sel.mask[i]) continue;
    const pi = i * 4;
    out.data[pi + 3] = 0;
  }
  // silence unused if w needed for future
  void w;
  return out;
}

/**
 * Crop source ImageData to an inclusive pixel rect.
 * @param {ImageData} source
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @returns {ImageData|null}
 */
export function cropImageData(source, x0, y0, x1, y1) {
  if (!source) return null;
  const left = Math.max(0, Math.min(Math.round(x0), Math.round(x1)));
  const right = Math.min(
    source.width - 1,
    Math.max(Math.round(x0), Math.round(x1)),
  );
  const top = Math.max(0, Math.min(Math.round(y0), Math.round(y1)));
  const bottom = Math.min(
    source.height - 1,
    Math.max(Math.round(y0), Math.round(y1)),
  );
  const w = right - left + 1;
  const h = bottom - top + 1;
  if (w < 1 || h < 1) return null;
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const si = ((top + y) * source.width + (left + x)) * 4;
      const di = (y * w + x) * 4;
      out.data[di] = source.data[si];
      out.data[di + 1] = source.data[si + 1];
      out.data[di + 2] = source.data[si + 2];
      out.data[di + 3] = source.data[si + 3];
    }
  }
  return out;
}

/**
 * Invert selection mask (complement). Works for any selection mode output.
 * @param {SelectionMask} sel
 * @returns {SelectionMask}
 */
export function invertSelectionMask(sel) {
  if (!sel?.mask) return createEmptySelection(1, 1);
  const out = createEmptySelection(sel.width, sel.height);
  for (let i = 0; i < sel.mask.length; i += 1) {
    out.mask[i] = sel.mask[i] ? 0 : 1;
  }
  return out;
}

/**
 * Select entire canvas bounds.
 * @param {number} width
 * @param {number} height
 * @returns {SelectionMask}
 */
export function selectAllMask(width, height) {
  return rectSelectionMask(width, height, 0, 0, width - 1, height - 1);
}

/**
 * @typedef {'nw'|'n'|'ne'|'w'|'center'|'e'|'sw'|'s'|'se'} CanvasAnchor
 */

/**
 * Offset to place old content on a resized canvas for a given anchor.
 * @param {number} oldW
 * @param {number} oldH
 * @param {number} newW
 * @param {number} newH
 * @param {CanvasAnchor} anchor
 * @returns {{x:number,y:number}}
 */
export function canvasAnchorOffset(oldW, oldH, newW, newH, anchor) {
  const dx = newW - oldW;
  const dy = newH - oldH;
  switch (anchor) {
    case 'nw':
      return { x: 0, y: 0 };
    case 'n':
      return { x: Math.floor(dx / 2), y: 0 };
    case 'ne':
      return { x: dx, y: 0 };
    case 'w':
      return { x: 0, y: Math.floor(dy / 2) };
    case 'e':
      return { x: dx, y: Math.floor(dy / 2) };
    case 'sw':
      return { x: 0, y: dy };
    case 's':
      return { x: Math.floor(dx / 2), y: dy };
    case 'se':
      return { x: dx, y: dy };
    case 'center':
    default:
      return { x: Math.floor(dx / 2), y: Math.floor(dy / 2) };
  }
}

/**
 * Resize canvas without scaling pixels (expand = transparent, shrink = clip).
 * @param {ImageData} source
 * @param {number} newWidth
 * @param {number} newHeight
 * @param {CanvasAnchor} [anchor]
 * @returns {ImageData|null}
 */
export function resizeCanvasImageData(source, newWidth, newHeight, anchor = 'center') {
  if (!source) return null;
  const w = Math.max(1, Math.round(Number(newWidth) || 1));
  const h = Math.max(1, Math.round(Number(newHeight) || 1));
  const out = new ImageData(w, h);
  const { x: ox, y: oy } = canvasAnchorOffset(
    source.width,
    source.height,
    w,
    h,
    anchor,
  );
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      const dx = x + ox;
      const dy = y + oy;
      if (dx < 0 || dy < 0 || dx >= w || dy >= h) continue;
      const si = (y * source.width + x) * 4;
      const di = (dy * w + dx) * 4;
      out.data[di] = source.data[si];
      out.data[di + 1] = source.data[si + 1];
      out.data[di + 2] = source.data[si + 2];
      out.data[di + 3] = source.data[si + 3];
    }
  }
  return out;
}

/**
 * Scale image content to new pixel dimensions (smooth bilinear via canvas).
 * @param {ImageData} source
 * @param {number} newWidth
 * @param {number} newHeight
 * @param {{ smooth?: boolean }} [opts]
 * @returns {ImageData|null}
 */
export function scaleImageData(source, newWidth, newHeight, opts = {}) {
  if (!source || typeof document === 'undefined') return null;
  const w = Math.max(1, Math.round(Number(newWidth) || 1));
  const h = Math.max(1, Math.round(Number(newHeight) || 1));
  const src = document.createElement('canvas');
  src.width = source.width;
  src.height = source.height;
  const sctx = src.getContext('2d');
  if (!sctx) return null;
  sctx.putImageData(source, 0, 0);
  const dest = document.createElement('canvas');
  dest.width = w;
  dest.height = h;
  const dctx = dest.getContext('2d');
  if (!dctx) return null;
  dctx.imageSmoothingEnabled = opts.smooth !== false;
  dctx.imageSmoothingQuality = 'high';
  dctx.clearRect(0, 0, w, h);
  dctx.drawImage(src, 0, 0, w, h);
  return dctx.getImageData(0, 0, w, h);
}
